import { showError } from '@components/app-error';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { HeadingSecondary } from '@course/components/headings';
import { DefaultSpinner } from '@components/spinner';
import { showToast } from '@components/toaster';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { useState } from 'preact/hooks';
import { FullCourse } from 'server/types';
import { SetPasswordEmailKind } from 'shared/set-password-email';

const store = rpx.courseSettings;

type Settings = RpxResponse<typeof store.getSetPasswordEmail>;

const COPY: Record<SetPasswordEmailKind, { heading: string; intro: string }> = {
  initial: {
    heading: 'Welcome email (account created for them)',
    intro:
      "Sent to people whose account the platform created for them (a checkout that skipped creating a password, or an enrollment from Ontraport), and when staff re-send it from a student's page.",
  },
  reminder: {
    heading: 'Reminder email (never opened the course)',
    intro:
      'Sent only when staff choose to: to one student from their page, or to everyone who has never opened the course, from the Progress tab. Its button signs them straight in (creating a password first if they never have).',
  },
};

/**
 * Both "set your password" emails for a course: the initial one and the
 * reminder. Blank fields use the built-in wording, shown as placeholders.
 */
export function SetPasswordEmailSettings({ course }: { course: Pick<FullCourse, 'id' | 'title'> }) {
  return (
    <>
      <SetPasswordEmailEditor course={course} kind="initial" />
      <SetPasswordEmailEditor course={course} kind="reminder" />
    </>
  );
}

function SetPasswordEmailEditor({
  course,
  kind,
}: {
  course: Pick<FullCourse, 'id' | 'title'>;
  kind: SetPasswordEmailKind;
}) {
  const [settings, setSettings] = useState<Settings | undefined>();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [preview, setPreview] = useState<{ subject: string; html: string } | undefined>();

  async function load() {
    const result = await store.getSetPasswordEmail({ courseId: course.id, kind });
    setSettings(result);
    setSubject(result.subject);
    setBody(result.body);
  }

  useAsyncEffect(load, [course.id, kind]);

  async function save() {
    setIsSaving(true);
    try {
      await store.setSetPasswordEmail({ courseId: course.id, kind, subject, body });
      await load();
      showToast({
        title: 'Saved',
        message: `The ${COPY[kind].heading.toLowerCase()} for ${course.title} was saved.`,
        type: 'ok',
      });
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  }

  if (!settings) {
    return <DefaultSpinner />;
  }

  return (
    <section class="flex flex-col gap-4 mt-10">
      <HeadingSecondary>{COPY[kind].heading}</HeadingSecondary>
      <p class="text-gray-600 text-sm">
        {COPY[kind].intro} The "Set your password" button is added automatically after your text.
        Leave a field blank to use the built-in wording.
      </p>
      <p class="text-gray-600 text-sm">
        Placeholders:{' '}
        {settings.placeholders.map((p, i) => (
          <span key={p}>
            {i > 0 && ', '}
            <code>{`{{{${p}}}}`}</code>
          </span>
        ))}
      </p>
      <label class="flex flex-col gap-1">
        <span class="font-medium">Subject</span>
        <input
          class="ruz-input"
          type="text"
          value={subject}
          placeholder={settings.defaults.subject}
          onInput={(e) => setSubject((e.target as HTMLInputElement).value)}
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="font-medium">Body</span>
        <textarea
          class="ruz-input"
          rows={8}
          value={body}
          placeholder={settings.defaults.body}
          onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
        />
        <span class="text-xs text-gray-500">A blank line starts a new paragraph.</span>
      </label>
      <div class="flex gap-2">
        <BtnPrimary isLoading={isSaving} onClick={save}>
          Save
        </BtnPrimary>
        <BtnSecondary onClick={() => setPreview(settings.preview)}>
          Preview saved version
        </BtnSecondary>
      </div>
      {preview && (
        <div class="flex flex-col gap-2 border rounded-sm p-4 bg-gray-50">
          <div class="flex items-center justify-between">
            <span>
              <span class="text-gray-500">Subject:</span> <strong>{preview.subject}</strong>
            </span>
            <BtnSecondary onClick={() => setPreview(undefined)}>Close preview</BtnSecondary>
          </div>
          <iframe
            title={`${COPY[kind].heading} preview`}
            class="w-full bg-white border rounded-sm"
            style="height: 32rem"
            srcdoc={preview.html}
          />
        </div>
      )}
    </section>
  );
}
